'use client';

import { c as _c } from "react/compiler-runtime";
import { BackgroundLogo } from '@/components/background-logo';
import { AppErrorFallback } from '@/components/error-boundary/app-error-fallback';
import { FooterClientWrapper } from '@/components/footer/footer-client-wrapper';
import { GetTheAppProvider } from '@/components/get-the-app';
import { HeaderActionsProvider } from '@/components/header/header-actions';
import { HeaderClientWrapper } from '@/components/header/header-client-wrapper';
import { HideHeaderProvider } from '@/components/header/hide-header-context';
import { PageBar, PageBarProvider } from '@/components/header/page-bar';
import { NativePushProvider } from '@/components/native-push-provider';
import { OfflineBanner } from '@/components/offline-banner';
import { useHideBackgroundLogo } from '@/components/ui/hide-background-logo-context';
import { ClientProviders } from '@/context/client-providers';
import { OfflineQueueSync } from '@/features/chat/hooks/use-offline-queue-processor';
import { OnboardingRecheck } from '@/features/onboarding/components/onboarding-recheck';
import { PostHogProvider } from '@/providers/post-hog-provider';
import { TRPCProvider } from '@/trpc/client';
import type { Locale, NavigationMode } from '@/types/types';
import { cn } from '@/utils/tailwindcss-override';
import type { ReactNode } from 'react';
import React from 'react';
import { ErrorBoundary } from 'react-error-boundary';
interface AppShellProperties {
  children: ReactNode;
  header: ReactNode;
  footer?: ReactNode;
  inAppDesign: boolean;
  locale: Locale;
  swUrl?: string;
  navigationMode?: NavigationMode;
}

/**
 * Unified App Shell component that provides all necessary providers
 * and layout structure for both online and offline versions.
 */
export const AppShell: React.FC<AppShellProperties> = t0 => {
  const $ = _c(35);
  const {
    children,
    header,
    footer,
    inAppDesign,
    locale,
    navigationMode: t1
  } = t0;
  const navigationMode = t1 === undefined ? "side-nav" : t1;
  const {
    hideBackgroundLogo
  } = useHideBackgroundLogo();
  const isTopNavMode = navigationMode === "top-nav";
  let t2;
  let t3;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <OfflineQueueSync />;
    t3 = <OnboardingRecheck />;
    $[0] = t2;
    $[1] = t3;
  } else {
    t2 = $[0];
    t3 = $[1];
  }
  let t4;
  if ($[2] !== header) {
    t4 = <HeaderClientWrapper>{header}</HeaderClientWrapper>;
    $[2] = header;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  let t5;
  if ($[4] !== hideBackgroundLogo || $[5] !== inAppDesign || $[6] !== isTopNavMode) {
    t5 = !hideBackgroundLogo && !inAppDesign && <div className={cn("absolute top-0 z-[-999] h-screen w-full p-[56px]", isTopNavMode ? "lg:hidden" : "xl:pl-[480px]")}><BackgroundLogo className="max-h-[60vh]" /></div>;
    $[4] = hideBackgroundLogo;
    $[5] = inAppDesign;
    $[6] = isTopNavMode;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  const t6 = inAppDesign ? "[--app-tab-bar-height:5rem]" : "[--app-tab-bar-height:0px]";
  const t7 = isTopNavMode ? "xl:[--app-content-top:calc(4rem+2px+var(--app-top-inset))] xl:[--app-header-height:calc(4rem+var(--app-top-inset))]" : "xl:ml-[480px] xl:[--app-content-left:480px]";
  const t8 = isTopNavMode && inAppDesign && "xl:ml-20 xl:[--app-content-left:5rem] xl:[--app-tab-bar-height:0px]";
  let t9;
  if ($[8] !== t6 || $[9] !== t7 || $[10] !== t8) {
    t9 = cn("wco-content-wrapper mt-(--app-content-top) h-[calc(100dvh-var(--app-content-top))] [--app-content-left:0px] [--app-content-top:calc(62px+var(--app-top-inset))] [--app-header-height:calc(60px+var(--app-top-inset))]", t6, t7, t8);
    $[8] = t6;
    $[9] = t7;
    $[10] = t8;
    $[11] = t9;
  } else {
    t9 = $[11];
  }
  const t10 = !inAppDesign && "pl-28 xl:pl-5";
  let t11;
  if ($[12] !== t10) {
    t11 = cn("sticky top-(--app-header-height) z-40", t10);
    $[12] = t10;
    $[13] = t11;
  } else {
    t11 = $[13];
  }
  let t12;
  if ($[14] !== locale || $[15] !== t11) {
    t12 = <OfflineBanner locale={locale} className={t11} />;
    $[14] = locale;
    $[15] = t11;
    $[16] = t12;
  } else {
    t12 = $[16];
  }
  let t13;
  if ($[17] !== inAppDesign) {
    t13 = <PageBar inAppDesign={inAppDesign} />;
    $[17] = inAppDesign;
    $[18] = t13;
  } else {
    t13 = $[18];
  }
  let t14;
  if ($[19] !== children) {
    t14 = <div className="flex-1"><ErrorBoundary FallbackComponent={AppErrorFallback}>{children}</ErrorBoundary></div>;
    $[19] = children;
    $[20] = t14;
  } else {
    t14 = $[20];
  }
  let t15;
  if ($[21] !== footer) {
    t15 = footer !== null && footer !== undefined && <FooterClientWrapper>{footer}</FooterClientWrapper>;
    $[21] = footer;
    $[22] = t15;
  } else {
    t15 = $[22];
  }
  let t16;
  if ($[23] !== t12 || $[24] !== t13 || $[25] !== t14 || $[26] !== t15) {
    t16 = <main className="flex min-h-full flex-col justify-between">{t12}{t13}{t14}{t15}</main>;
    $[23] = t12;
    $[24] = t13;
    $[25] = t14;
    $[26] = t15;
    $[27] = t16;
  } else {
    t16 = $[27];
  }
  let t17;
  if ($[28] !== t16 || $[29] !== t9) {
    t17 = <div className={t9}>{t16}</div>;
    $[28] = t16;
    $[29] = t9;
    $[30] = t17;
  } else {
    t17 = $[30];
  }
  let t18;
  if ($[31] !== t17 || $[32] !== t4 || $[33] !== t5) {
    t18 = <PostHogProvider><TRPCProvider><ClientProviders>{t2}{t3}<NativePushProvider><HideHeaderProvider><HeaderActionsProvider><PageBarProvider><GetTheAppProvider>{t4}{t5}{t17}</GetTheAppProvider></PageBarProvider></HeaderActionsProvider></HideHeaderProvider></NativePushProvider></ClientProviders></TRPCProvider></PostHogProvider>;
    $[31] = t17;
    $[32] = t4;
    $[33] = t5;
    $[34] = t18;
  } else {
    t18 = $[34];
  }
  return t18;
};